import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Badge, Button, Form, Modal, Table } from 'react-bootstrap';
import { OpportunityDocument, OpportunityFolderTreeNode } from '../types';
import './OpportunityDocumentsPanel.css';

const SAFE_EXT = /^[a-z0-9]{1,8}$/i;

const extensionFromBasename = (name: string): string | null => {
  const base = (name || '').split(/[/\\]/).pop() || '';
  const dot = base.lastIndexOf('.');
  if (dot < 0 || dot === base.length - 1) return null;
  const ext = base.slice(dot + 1).toLowerCase();
  return SAFE_EXT.test(ext) ? ext : null;
};

const extensionFromPublicPath = (publicPath: string): string | null => {
  try {
    const pathOnly = publicPath.split('?')[0] || '';
    const decoded = decodeURIComponent(pathOnly);
    return extensionFromBasename(decoded);
  } catch {
    return extensionFromBasename(publicPath);
  }
};

const describeDocumentType = (
  doc: OpportunityDocument
): { label: string; title: string } => {
  const rawMime = (doc.mime_type || '').trim().toLowerCase();
  const mime = rawMime.split(';')[0]?.trim() || '';
  const fromPath = extensionFromPublicPath(doc.file_path || '');
  const fromName = extensionFromBasename(doc.filename || '');
  const ext = fromPath || fromName;

  const mimeLabels: Record<string, string> = {
    'application/pdf': 'PDF',
    'application/msword': 'DOC',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
    'application/vnd.ms-excel': 'XLS',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'XLSX',
    'application/vnd.ms-powerpoint': 'PPT',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PPTX',
    'text/plain': 'TXT',
    'application/zip': 'ZIP',
    'application/x-zip-compressed': 'ZIP',
    'application/vnd.rar': 'RAR',
    'image/jpeg': 'JPEG',
    'image/jpg': 'JPEG',
    'image/png': 'PNG',
    'image/gif': 'GIF',
    'image/webp': 'WEBP',
  };

  if (mime && mimeLabels[mime]) {
    const bits = [mimeLabels[mime], mime];
    if (ext) bits.push(`.${ext}`);
    return { label: mimeLabels[mime], title: bits.join(' · ') };
  }

  if (mime && mime.includes('/')) {
    const subtype = mime.split('/')[1] || 'arquivo';
    const short = subtype.replace(/[^a-z0-9]+/gi, '').slice(0, 5).toUpperCase() || 'ARQ';
    return { label: short, title: mime };
  }

  if (ext) {
    return { label: ext.toUpperCase(), title: `Extensão inferida: .${ext}` };
  }

  return { label: '—', title: 'Tipo não identificado' };
};

interface OpportunityDocumentsPanelProps {
  documents: OpportunityDocument[];
  folders: OpportunityFolderTreeNode[];
  selectedFolderId?: number | null;
  canEdit?: boolean;
  canDownload?: boolean;
  onUploadFile: (file: File) => void;
  onMoveDocument: (documentId: number, folderId?: number | null) => void;
  onBulkMoveDocuments: (documentIds: number[], folderId?: number | null) => void;
  onBulkDeleteDocuments: (documentIds: number[]) => void;
  onRenameDocument: (document: OpportunityDocument) => void;
  onDeleteDocument: (document: OpportunityDocument) => void;
  onDownloadDocument?: (document: OpportunityDocument) => void;
}

const OpportunityDocumentsPanel: React.FC<OpportunityDocumentsPanelProps> = ({
  documents,
  folders,
  selectedFolderId,
  canEdit = false,
  canDownload = false,
  onUploadFile,
  onMoveDocument,
  onBulkMoveDocuments,
  onBulkDeleteDocuments,
  onRenameDocument,
  onDeleteDocument,
  onDownloadDocument,
}) => {
  const flatFolders = useMemo(() => {
    const flatten = (
      nodes: OpportunityFolderTreeNode[],
      acc: OpportunityFolderTreeNode[] = []
    ): OpportunityFolderTreeNode[] => {
      for (const node of nodes) {
        acc.push(node);
        flatten(node.children, acc);
      }
      return acc;
    };
    return flatten(folders);
  }, [folders]);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState<number[]>([]);
  const [bulkTargetFolderId, setBulkTargetFolderId] = useState<string>('none');
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveDocumentId, setMoveDocumentId] = useState<number | null>(null);
  const [moveTargetFolderId, setMoveTargetFolderId] = useState<string>('none');
  const [actionMenu, setActionMenu] = useState<{
    document: OpportunityDocument;
    top: number;
    left: number;
  } | null>(null);

  const selectedCount = selectedDocumentIds.length;
  const allVisibleSelected = useMemo(
    () => documents.length > 0 && documents.every((doc) => selectedDocumentIds.includes(doc.id)),
    [documents, selectedDocumentIds]
  );

  const toggleSelectDocument = (documentId: number) => {
    setSelectedDocumentIds((prev) =>
      prev.includes(documentId) ? prev.filter((id) => id !== documentId) : [...prev, documentId]
    );
  };

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedDocumentIds((prev) => prev.filter((id) => !documents.some((doc) => doc.id === id)));
      return;
    }
    setSelectedDocumentIds((prev) => {
      const set = new Set(prev);
      documents.forEach((doc) => set.add(doc.id));
      return Array.from(set);
    });
  };

  const getFolderIdFromSelect = (value: string): number | null => {
    if (value === 'none') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const openMoveDocumentModal = (document: OpportunityDocument) => {
    setMoveDocumentId(document.id);
    setMoveTargetFolderId(document.folder_id == null ? 'none' : String(document.folder_id));
    setShowMoveModal(true);
  };

  const openActionMenu = (
    document: OpportunityDocument,
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 150;
    const menuHeight = 168;
    const left = Math.max(8, Math.min(window.innerWidth - menuWidth - 8, rect.left - menuWidth - 8));
    const top = Math.max(8, Math.min(window.innerHeight - menuHeight - 8, rect.top));
    setActionMenu((current) => (
      current?.document.id === document.id ? null : { document, top, left }
    ));
  };

  const confirmMoveDocument = () => {
    if (moveDocumentId == null) return;
    onMoveDocument(moveDocumentId, getFolderIdFromSelect(moveTargetFolderId));
    setShowMoveModal(false);
    setMoveDocumentId(null);
  };

  useEffect(() => {
    const visibleIds = new Set(documents.map((doc) => doc.id));
    setSelectedDocumentIds((prev) => prev.filter((id) => visibleIds.has(id)));
    setActionMenu(null);
  }, [documents]);

  useEffect(() => {
    const closeMenu = () => setActionMenu(null);
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        closeMenu();
        return;
      }
      if (
        target.closest('.document-actions-portal-menu') ||
        target.closest('.opportunity-documents-actions-toggle')
      ) {
        return;
      }
      closeMenu();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeMenu();
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    window.addEventListener('resize', closeMenu);
    window.addEventListener('scroll', closeMenu, true);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('resize', closeMenu);
      window.removeEventListener('scroll', closeMenu, true);
    };
  }, []);

  return (
    <div className="opportunity-documents-panel">
      <div className="opportunity-documents-toolbar d-flex flex-column flex-xl-row justify-content-between align-items-xl-center gap-2 mb-3">
        <h6 className="mb-0 d-flex align-items-center gap-2">
          Documentos{' '}
          <Badge bg="secondary">
            {selectedFolderId ? 'Pasta selecionada' : 'Raiz da oportunidade'}
          </Badge>
          <Badge bg="light" text="dark">
            Selecionados: {selectedCount}
          </Badge>
        </h6>
        <div className="d-flex flex-column flex-lg-row gap-2 opportunity-documents-actions">
          <Form.Select
            size="sm"
            className="opportunity-documents-bulk-select"
            value={bulkTargetFolderId}
            disabled={!canEdit || selectedCount === 0}
            onChange={(event) => setBulkTargetFolderId(event.target.value)}
          >
            <option value="none">Mover selecionados para: Raiz da oportunidade</option>
            {flatFolders.map((folder) => (
              <option key={`bulk-${folder.id}`} value={folder.id}>
                {folder.name}
              </option>
            ))}
          </Form.Select>
          <Button
            size="sm"
            variant="outline-primary"
            className="opportunity-documents-bulk-button"
            disabled={!canEdit || selectedCount === 0}
            onClick={() => onBulkMoveDocuments(selectedDocumentIds, getFolderIdFromSelect(bulkTargetFolderId))}
          >
            Mover selecionados ({selectedCount})
          </Button>
          <Button
            size="sm"
            variant="outline-danger"
            className="opportunity-documents-bulk-button"
            disabled={!canEdit || selectedCount === 0}
            onClick={() => onBulkDeleteDocuments(selectedDocumentIds)}
          >
            Excluir selecionados ({selectedCount})
          </Button>
          <Form.Group controlId="upload-opportunity-document" className="mb-0 opportunity-documents-upload-group">
            <Form.Label className="small text-muted mb-1">Enviar arquivo (máx. 100 MB)</Form.Label>
            <Form.Control
              type="file"
              size="sm"
              className="opportunity-documents-upload-input"
              disabled={!canEdit}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const file = event.currentTarget.files?.[0];
                if (file) {
                  onUploadFile(file);
                  event.currentTarget.value = '';
                }
              }}
            />
          </Form.Group>
        </div>
      </div>

      <div className="opportunity-documents-table-wrapper">
        <Table striped hover size="sm" className="align-middle opportunity-documents-table">
          <thead>
            <tr>
              <th style={{ width: 40 }}>
                <Form.Check
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleSelectAllVisible}
                  disabled={documents.length === 0}
                />
              </th>
              <th>Arquivo</th>
              <th className="opportunity-documents-type-col">Tipo</th>
              <th>Caminho</th>
              <th>Data do envio</th>
              <th className="text-end opportunity-documents-actions-col">Ações</th>
            </tr>
          </thead>
          <tbody>
            {documents.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center text-muted py-3">
                  Nenhum documento nesta pasta.
                </td>
              </tr>
            ) : (
              documents.map((doc) => {
                const typeInfo = describeDocumentType(doc);
                return (
                <tr key={doc.id}>
                  <td>
                    <Form.Check
                      type="checkbox"
                      checked={selectedDocumentIds.includes(doc.id)}
                      onChange={() => toggleSelectDocument(doc.id)}
                    />
                  </td>
                  <td>
                    {canDownload && onDownloadDocument ? (
                    <button
                      type="button"
                      className="opportunity-documents-file-name-btn opportunity-documents-file-link"
                      title={`Baixar: ${doc.filename}`}
                      onClick={() => onDownloadDocument(doc)}
                    >
                      <i className="bi bi-file-earmark-arrow-down opportunity-documents-file-icon" aria-hidden />
                      <span className="opportunity-documents-filename">{doc.filename}</span>
                    </button>
                    ) : (
                    <span className="opportunity-documents-file-link text-muted" title={doc.filename}>
                      <i className="bi bi-file-earmark-text opportunity-documents-file-icon" aria-hidden />
                      <span className="opportunity-documents-filename">{doc.filename}</span>
                    </span>
                    )}
                  </td>
                  <td className="opportunity-documents-type-col">
                    <span className="d-inline-block" title={typeInfo.title}>
                      <Badge
                        bg="light"
                        text="dark"
                        className="opportunity-documents-type-badge"
                      >
                        {typeInfo.label}
                      </Badge>
                    </span>
                  </td>
                  <td>
                    <span className="opportunity-documents-folder-path" title={doc.folder_path || 'Raiz da oportunidade'}>
                      {doc.folder_path || 'Raiz da oportunidade'}
                    </span>
                  </td>
                  <td>{new Date(doc.uploaded_at).toLocaleString('pt-BR')}</td>
                  <td className="text-end opportunity-documents-actions-col">
                    <button
                      type="button"
                      className="opportunity-documents-actions-toggle"
                      disabled={!canEdit && !canDownload}
                      title="Ações do arquivo"
                      aria-label={`Ações do arquivo ${doc.filename}`}
                      aria-expanded={actionMenu?.document.id === doc.id}
                      onClick={(event) => openActionMenu(doc, event)}
                    >
                      <span className="opportunity-documents-actions-icon" aria-hidden>
                        ⋯
                      </span>
                      <span className="visually-hidden">Ações</span>
                    </button>
                  </td>
                </tr>
              );
              })
            )}
          </tbody>
        </Table>
      </div>

      {actionMenu && createPortal(
        <div
          className="document-actions-portal-menu"
          style={{ top: actionMenu.top, left: actionMenu.left }}
          role="menu"
        >
          {canDownload && onDownloadDocument && (
          <button
            type="button"
            className="document-actions-portal-item"
            onClick={() => {
              const selected = actionMenu.document;
              setActionMenu(null);
              onDownloadDocument(selected);
            }}
          >
            Baixar
          </button>
          )}
          <button
            type="button"
            className="document-actions-portal-item"
            disabled={!canEdit}
            onClick={() => {
              const selected = actionMenu.document;
              setActionMenu(null);
              onRenameDocument(selected);
            }}
          >
            Renomear
          </button>
          <button
            type="button"
            className="document-actions-portal-item"
            disabled={!canEdit}
            onClick={() => {
              const selected = actionMenu.document;
              setActionMenu(null);
              openMoveDocumentModal(selected);
            }}
          >
            Mover
          </button>
          <div className="document-actions-portal-divider" />
          <button
            type="button"
            className="document-actions-portal-item document-actions-portal-item-danger"
            disabled={!canEdit}
            onClick={() => {
              const selected = actionMenu.document;
              setActionMenu(null);
              onDeleteDocument(selected);
            }}
          >
            Excluir
          </button>
        </div>,
        document.body
      )}

      <Modal show={showMoveModal} onHide={() => setShowMoveModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Mover arquivo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Select
            value={moveTargetFolderId}
            onChange={(event) => setMoveTargetFolderId(event.target.value)}
          >
            <option value="none">Raiz da oportunidade</option>
            {flatFolders.map((folder) => (
              <option key={`move-doc-${folder.id}`} value={folder.id}>
                {folder.name}
              </option>
            ))}
          </Form.Select>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowMoveModal(false)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={confirmMoveDocument}>
            Mover
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default OpportunityDocumentsPanel;
